import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Check, ChevronDown, ChevronRight, Flag as FlagIcon, History, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RelativeTime } from "@/components/RelativeTime";

const BASE_SCOPES = ["production", "staging", "preview", "development"] as const;
const KEY_RE = /^[a-z0-9][a-z0-9-_]{0,63}$/;

interface FlagValue {
  scope: string;
  enabled: boolean;
  rolloutPercent: number | null;
}

interface FeatureFlag {
  id: string;
  key: string;
  description: string | null;
  defaultOn: boolean;
  createdAt: string;
  values: FlagValue[];
}

interface CustomEnvironment {
  id: string;
  name: string;
}

interface FlagChange {
  id: string;
  actorEmail: string | null;
  scope: string | null;
  old: unknown;
  new: unknown;
  ts: string;
}

/**
 * Feature flags tab — per-project flags with per-scope overrides across the
 * four base environments plus any custom environments. Values are read by
 * apps at request time (OPENFRONT_FLAGS_URL / OPENFRONT_FLAGS_TOKEN), so no
 * redeploy is needed when toggling.
 */
export function FeatureFlags() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [openFlagId, setOpenFlagId] = useState<string | null>(null);
  const queryKey = ["feature-flags", org, project] as const;

  const flagsQ = useQuery({
    queryKey,
    queryFn: () =>
      api.get<{ flags: FeatureFlag[]; customEnvironments: CustomEnvironment[] }>(
        `/orgs/${org}/projects/${project}/flags`,
      ),
  });
  const flags = flagsQ.data?.flags ?? [];
  const customEnvs = flagsQ.data?.customEnvironments ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/projects/${project}/flags/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <FlagIcon className="h-3.5 w-3.5 text-muted-foreground" /> Feature flags
        </h2>
        <p className="text-xs text-muted-foreground">
          Toggle behavior per environment without redeploying. Apps read values at request time via the injected{" "}
          <span className="font-mono">OPENFRONT_FLAGS_URL</span> /{" "}
          <span className="font-mono">OPENFRONT_FLAGS_TOKEN</span> env vars.
        </p>
      </div>

      <CreateFlagForm
        orgSlug={org!}
        projectSlug={project!}
        onCreated={() => qc.invalidateQueries({ queryKey })}
      />

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
            <FlagIcon className="h-3.5 w-3.5 text-muted-foreground" /> Flags
          </h3>
          <span className="text-2xs text-subtle-foreground">{flags.length} / 50</span>
        </div>
        {flagsQ.isLoading && <Empty>Loading…</Empty>}
        {flagsQ.error instanceof ApiError && <Empty>{flagsQ.error.message}</Empty>}
        {!flagsQ.isLoading && !flagsQ.error && flags.length === 0 && <Empty>No flags yet.</Empty>}
        {flags.map((f) => (
          <div key={f.id} className="border-b border-border-muted last:border-b-0">
            <div className="of-row gap-3 items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpenFlagId(openFlagId === f.id ? null : f.id)}
                title="Toggle details"
              >
                {openFlagId === f.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </Button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold truncate">{f.key}</span>
                  {f.defaultOn ? (
                    <span className="text-2xs uppercase tracking-wider bg-success-subtle text-success border border-success/30 rounded px-1.5 py-0.5">
                      default on
                    </span>
                  ) : (
                    <span className="text-2xs uppercase tracking-wider bg-subtle text-subtle-foreground border border-border-muted rounded px-1.5 py-0.5">
                      default off
                    </span>
                  )}
                </div>
                {f.description && (
                  <div className="text-2xs text-muted-foreground mt-0.5 truncate">{f.description}</div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-danger"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete flag '${f.key}'? Apps will stop receiving it immediately.`)) {
                    remove.mutate(f.id);
                  }
                }}
              >
                {remove.isPending && remove.variables === f.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
            {openFlagId === f.id && (
              <FlagPanel orgSlug={org!} projectSlug={project!} flag={f} customEnvs={customEnvs} queryKey={queryKey} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateFlagForm({ orgSlug, projectSlug, onCreated }: {
  orgSlug: string;
  projectSlug: string;
  onCreated: () => void;
}) {
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [defaultOn, setDefaultOn] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      api.post<{ flag: FeatureFlag }>(`/orgs/${orgSlug}/projects/${projectSlug}/flags`, {
        key,
        description: description.trim() || undefined,
        defaultOn,
      }),
    onSuccess: () => {
      onCreated();
      setKey("");
      setDescription("");
      setDefaultOn(false);
    },
  });
  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Create flag
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-2">
          <div className="space-y-1">
            <Label htmlFor="flag-key" className="text-xs">Key</Label>
            <Input
              id="flag-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="new-checkout"
              className="font-mono text-sm"
            />
            <div className="text-2xs text-subtle-foreground">a-z 0-9 - _ (max 64, starts with a letter or digit)</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="flag-desc" className="text-xs">Description</Label>
            <Input
              id="flag-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              className="text-sm"
            />
          </div>
        </div>
        <label className="text-xs flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={defaultOn} onChange={(e) => setDefaultOn(e.target.checked)} />
          Default on
        </label>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => create.mutate()}
            disabled={!KEY_RE.test(key) || create.isPending}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Create flag
          </Button>
        </div>
      </div>
    </details>
  );
}

function FlagPanel({ orgSlug, projectSlug, flag, customEnvs, queryKey }: {
  orgSlug: string;
  projectSlug: string;
  flag: FeatureFlag;
  customEnvs: CustomEnvironment[];
  queryKey: readonly unknown[];
}) {
  const [showHistory, setShowHistory] = useState(false);
  const scopes: Array<{ scope: string; label: string }> = [
    ...BASE_SCOPES.map((s) => ({ scope: s, label: s })),
    ...customEnvs.map((e) => ({ scope: `env:${e.id}`, label: `env:${e.name}` })),
  ];
  const scopeLabel = (scope: string | null) =>
    scope === null ? null : scopes.find((s) => s.scope === scope)?.label ?? scope;

  return (
    <div className="bg-subtle/50 border-t border-border-muted px-4 py-3 space-y-3">
      <div className="space-y-1.5">
        {scopes.map((s) => (
          <ScopeLine
            key={s.scope}
            orgSlug={orgSlug}
            projectSlug={projectSlug}
            flagId={flag.id}
            scope={s.scope}
            label={s.label}
            value={flag.values.find((v) => v.scope === s.scope)}
            queryKey={queryKey}
          />
        ))}
      </div>
      <div>
        <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
          <History className="h-3.5 w-3.5" /> History
        </Button>
      </div>
      {showHistory && (
        <FlagHistory orgSlug={orgSlug} projectSlug={projectSlug} flagId={flag.id} scopeLabel={scopeLabel} />
      )}
    </div>
  );
}

function ScopeLine({ orgSlug, projectSlug, flagId, scope, label, value, queryKey }: {
  orgSlug: string;
  projectSlug: string;
  flagId: string;
  scope: string;
  label: string;
  value: FlagValue | undefined;
  queryKey: readonly unknown[];
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"default" | "on" | "off">(value ? (value.enabled ? "on" : "off") : "default");
  const [rollout, setRollout] = useState(value?.rolloutPercent != null ? String(value.rolloutPercent) : "");

  const rolloutNum = rollout.trim() === "" ? null : Number(rollout);
  const rolloutValid =
    rolloutNum === null || (Number.isInteger(rolloutNum) && rolloutNum >= 0 && rolloutNum <= 100);

  const save = useMutation({
    mutationFn: () =>
      mode === "default"
        ? api.delete(
            `/orgs/${orgSlug}/projects/${projectSlug}/flags/${flagId}/value?scope=${encodeURIComponent(scope)}`,
          )
        : api.put(`/orgs/${orgSlug}/projects/${projectSlug}/flags/${flagId}/value`, {
            scope,
            enabled: mode === "on",
            rolloutPercent: mode === "on" ? rolloutNum : null,
          }),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-40 truncate font-mono text-xs">{label}</span>
      <span className="w-32 text-xs">
        {value ? (
          <span className="font-mono">
            {value.enabled ? "on" : "off"}
            {value.enabled && value.rolloutPercent != null && ` · ${value.rolloutPercent}%`}
          </span>
        ) : (
          <span className="italic text-muted-foreground">inherits default</span>
        )}
      </span>
      <select
        className="h-9 px-3 rounded-md border border-border bg-background text-sm font-mono"
        value={mode}
        onChange={(e) => setMode(e.target.value as "default" | "on" | "off")}
      >
        <option value="default">default</option>
        <option value="on">on</option>
        <option value="off">off</option>
      </select>
      {mode === "on" && (
        <span className="inline-flex items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={rollout}
            onChange={(e) => setRollout(e.target.value)}
            placeholder="100"
            className="w-20 font-mono text-sm"
          />
          <span className="text-xs text-muted-foreground">%</span>
        </span>
      )}
      <Button
        size="sm"
        variant="default"
        onClick={() => save.mutate()}
        disabled={save.isPending || (mode === "on" && !rolloutValid)}
      >
        {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        Save
      </Button>
      {save.error instanceof ApiError && <span className="text-2xs text-danger">{save.error.message}</span>}
    </div>
  );
}

function FlagHistory({ orgSlug, projectSlug, flagId, scopeLabel }: {
  orgSlug: string;
  projectSlug: string;
  flagId: string;
  scopeLabel: (scope: string | null) => string | null;
}) {
  const q = useQuery({
    queryKey: ["flag-history", flagId],
    queryFn: () =>
      api
        .get<{ changes: FlagChange[] }>(`/orgs/${orgSlug}/projects/${projectSlug}/flags/${flagId}/history`)
        .then((r) => r.changes),
  });
  return (
    <div className="space-y-1">
      {q.isLoading && <div className="text-2xs text-muted-foreground">Loading history…</div>}
      {q.error instanceof ApiError && <div className="text-2xs text-danger">{q.error.message}</div>}
      {!q.isLoading && !q.error && (q.data?.length ?? 0) === 0 && (
        <div className="text-2xs text-muted-foreground">No changes recorded yet.</div>
      )}
      {q.data?.map((c) => (
        <div key={c.id} className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-2xs">
          <RelativeTime value={c.ts} />
          <span className="min-w-0 text-muted-foreground truncate">{c.actorEmail ?? "system"}</span>
          {c.scope !== null && (
            <span className="font-mono bg-subtle border border-border-muted rounded px-1 py-0.5">
              {scopeLabel(c.scope)}
            </span>
          )}
          <span className="w-full break-all sm:w-auto sm:flex-1 sm:truncate font-mono text-2xs text-subtle-foreground">
            {JSON.stringify(c.old)} → {JSON.stringify(c.new)}
          </span>
        </div>
      ))}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}
